import * as React from "react"
import { format } from "date-fns"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

export function DatePicker({
  id,
  value,
  onChange,
  placeholder = "Pick a date",
  disabled = false,
  className,
  ...props
}) {
  const [open, setOpen] = React.useState(false)

  // Convert string dates to Date objects
  const getDateObject = (val) => {
    if (!val) return null
    if (val instanceof Date) return val
    if (typeof val === 'string') {
      const parsed = new Date(val)
      return isNaN(parsed.getTime()) ? null : parsed
    }
    return null
  }

  const dateValue = getDateObject(value)
  const currentDate = dateValue || new Date()
  const [month, setMonth] = React.useState(currentDate.getMonth())
  const [year, setYear] = React.useState(currentDate.getFullYear())
  const [date, setDate] = React.useState(currentDate)

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ]

  // Generate years from 1900 to current year + 10
  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: currentYear - 1900 + 11 }, (_, i) => currentYear + 10 - i)

  // Update displayed month/year when value changes externally
  React.useEffect(() => {
    const dateObj = getDateObject(value)
    if (dateObj) {
      setMonth(dateObj.getMonth())
      setYear(dateObj.getFullYear())
      setDate(dateObj)
    }
  }, [value])

  // Update calendar view when month/year dropdowns change
  React.useEffect(() => {
    const newDate = new Date(year, month, 1)
    setDate(newDate)
  }, [month, year])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal h-[44px] rounded-[10px] border border-[#9CA3AF] hover:border-[#6B7280] focus:border-primary500",
            !dateValue && "text-gray-500",
            disabled && "opacity-50 cursor-not-allowed",
            className
          )}
          disabled={disabled}
          style={{
            fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            fontSize: '14px',
            fontWeight: 400,
            backgroundColor: disabled ? '#F9FAFB' : 'white'
          }}
        >
          {dateValue ? format(dateValue, "MM/dd/yyyy") : <span className="text-gray-400">{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3 bg-white border-2 border-gray-200 shadow-lg rounded-lg" align="start" style={{ zIndex: 9999 }}>
        <div className="bg-white rounded-lg">
          {/* Month and Year Selectors */}
          <div className="flex items-center justify-between mb-3 gap-2">
            <Select value={month.toString()} onValueChange={(val) => setMonth(parseInt(val))}>
              <SelectTrigger
                className="w-[140px] h-[38px] border-2 border-gray-200 rounded-[8px] bg-white hover:border-gray-300 focus:border-primary500"
                style={{
                  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                  fontSize: '14px',
                  fontWeight: 400
                }}
              >
                <SelectValue>{months[month]}</SelectValue>
              </SelectTrigger>
              <SelectContent className="bg-white border-2 border-gray-200 shadow-lg" style={{ zIndex: 10000 }}>
                {months.map((monthName, index) => (
                  <SelectItem
                    key={index}
                    value={index.toString()}
                    className="hover:bg-gray-50"
                    style={{
                      fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                      fontSize: '14px'
                    }}
                  >
                    {monthName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={year.toString()} onValueChange={(val) => setYear(parseInt(val))}>
              <SelectTrigger
                className="w-[100px] h-[38px] border-2 border-gray-200 rounded-[8px] bg-white hover:border-gray-300 focus:border-primary500"
                style={{
                  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                  fontSize: '14px',
                  fontWeight: 400
                }}
              >
                <SelectValue>{year}</SelectValue>
              </SelectTrigger>
              <SelectContent className="bg-white border-2 border-gray-200 shadow-lg max-h-[200px] overflow-y-auto" style={{ zIndex: 10000 }}>
                {years.map((yearOption) => (
                  <SelectItem
                    key={yearOption}
                    value={yearOption.toString()}
                    className="hover:bg-gray-50"
                    style={{
                      fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                      fontSize: '14px'
                    }}
                  >
                    {yearOption}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Calendar */}
          <Calendar
            mode="single"
            selected={dateValue}
            onSelect={(date) => {
              onChange(date)
              setOpen(false)
            }}
            month={date}
            onMonthChange={setDate}
            initialFocus
            disabled={props.maxDate ? (date) => date > props.maxDate : undefined}
            className="rounded-lg border-0"
            captionLayout="hidden"
            {...props}
          />
        </div>
      </PopoverContent>
    </Popover>
  )
}
